import {
    Enquiry,
    Job,
    MenuNode,
    StaffUtilisationDataSet,
    Task,
    Team,
    FilterSet, BaseStaffUtilisationDataSet, Role, MenuNodeCfgSet
} from "./types.ts";
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {VirtualisedGrid} from "./VirtualisedGrid.tsx";
import {UtilisationFilters} from "./UtilisationFilters.tsx";
import {Staff} from "../Components/BaseTypes.ts";
import {sumArray} from "../Components/Utils.ts";

interface PropTypes {
    utilisationData: BaseStaffUtilisationDataSet,
    tasks: Record<number, Task>
    staff: Record<string, Staff>
    teams: Team[],
    jobs: Job[],
    roles: Record<number, Role>,
    enqs: Record<number, Enquiry>
    filters: FilterSet,
    menuNodeCfgSet: MenuNodeCfgSet,
    staffScheduleOnly: boolean
}



const debounce = (callback, wait) => {
    let timeoutId = null;
    return (...args) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback(...args);
        }, wait);
    };
}
const saveStateToDB = debounce((state, staffScheduleOnly) => {
    const route = staffScheduleOnly ? 'saveCurrentStaffUtilisationReportState' : 'saveUtilisationReportState';
    fetch('/api/tasks/' + route, {
        method: 'POST',
        body: JSON.stringify(state)
    })
        .then(res => {
            if (res.redirected) return Promise.reject("Couldn't save utilisation report state because you're not logged on");
            if (!res.ok) return Promise.reject(res.status);
        })
        .catch(error => {
            console.log(error);
        });
}, 1000);

function Utilisation({
                         utilisationData,
                         tasks,
                         staff,
                         teams,
                         enqs,
                         jobs,
                         roles,
                         filters: _filters,
                         menuNodeCfgSet: _menuNodeCfgSet,
                         staffScheduleOnly
                     }: PropTypes) {

    if (staffScheduleOnly && _filters) _filters.graph = true;

    const [filters, setFilters] = useState<FilterSet>(
        _filters || {
            interval: 'weekly',
            units: 'hours',
            normalTasks: true,
            proposedTasks: true,
            overdueTasks: true,
            prospectTasks: true,
            leave: true,
            allocatedForecast: "allocated",
            graph: true
        }
    );

    const dateIndexesWithDateObjects = useMemo(() => {
        return utilisationData.dateIndexes.map(unixDate => new Date(unixDate * 1000));
    }, [])

    // const [menuNodesFlatToRenderSet, setMenuNodesFlatToRender] = useState<MenuNode[][]>([[], []]);

    console.time('utilis');
    const filteredUtilisationData = useMemo(() => generateUtilisationData(tasks, filters, utilisationData, staff, dateIndexesWithDateObjects, enqs), [filters]);
    console.timeEnd('utilis');


    console.time('nodes');
    const menuNodes: MenuNode[] = useMemo(() => generateMenuNodes(teams, staff, filteredUtilisationData, jobs, enqs, tasks, roles, staffScheduleOnly), [filters]);
    const cleanedupMenuNodeCfgs = useMemo(() => cleanupMenuNodeCfgs(_menuNodeCfgSet, menuNodes, staffScheduleOnly, tasks, filteredUtilisationData), []);

    console.timeEnd('nodes');

    const [menuNodeCfgSet, setMenuNodeCfgSet] = useState<MenuNodeCfgSet>(cleanedupMenuNodeCfgs);


    const menuNodesFlatToRenderSet = useMemo(() => generateMenuNodesFlatToRender(menuNodes, filters, menuNodeCfgSet, filteredUtilisationData), [filters, menuNodeCfgSet]);


    useEffect(() => {
        // if (menuNodeCfgSet && filters) {
        //     generateMenuNodes(teams, staff, filteredUtilisationData, jobs, enqs, tasks, roles, menuNodeCfgSet, staffScheduleOnly)
        //     setMenuNodesFlatToRender(generateMenuNodesFlatToRender(menuNodes, filters, menuNodeCfgSet, filteredUtilisationData));
        // }

        saveStateToDB({filters: filters, menuNodeCfgSet: menuNodeCfgSet}, staffScheduleOnly);

    }, [filters, menuNodeCfgSet])

    return (
        <>
            {/*<div className='h-100'>*/}
            <UtilisationFilters setFilters={setFilters} defaultFilters={filters} staffScheduleOnly={staffScheduleOnly}/>


            {/*<div className='d-flex flex-column'>*/}
            <VirtualisedGrid
                tasks={tasks}
                staff={staff}
                utilisationData={filteredUtilisationData}
                menuNodesFlatToRenderSet={menuNodesFlatToRenderSet}
                setMenuNodeCfgSet={setMenuNodeCfgSet}
                menuNodeCfgSet={menuNodeCfgSet}
                filters={filters}
                staffScheduleOnly={staffScheduleOnly}
            />
            {/*</div>*/}
            {/*</div>*/}
        </>

    )
}

function generateUtilisationData(tasks: Record<string, Task>, filters: FilterSet, baseUtilisationData: BaseStaffUtilisationDataSet, staff: Record<string, Staff>, dateIndexesWithDateObjects: Date[], enqs: Record<any, Enquiry>): StaffUtilisationDataSet {
    const utilisationData: StaffUtilisationDataSet = {
        dateIndexes: filters.interval === 'daily' ? dateIndexesWithDateObjects : [],
        workDayDates: baseUtilisationData.workDayDates,
        staff: {},
        group: {
            mel: {
                periodHours: [],
                leaveHours: {total: 0, date: []},
                allocatedHoursForPeriod: {total: 0, date: [], overdue: {total: 0}},
                total: 0
            }
        }
    };

    const dateIndexesForPeriodStartDates = {};
    const dateIndexToAdjustedIndexMap = [];

    Object.values(staff).forEach(staff => {
        if (!staff.teamId) return;
        if (!utilisationData.group[staff.teamId]) utilisationData.group[staff.teamId] = {
            periodHours: [],
            leaveHours: {total: 0, date: []},
            allocatedHoursForPeriod: {total: 0, date: [], overdue: {total: 0}},
            total: 0,
        }
        utilisationData.staff[staff.callName] = {
            normalHoursForPeriod: {
                overdue: baseUtilisationData.staff[staff.callName]?.['normalHoursForPeriod']?.['overdue'],
                date: [],
                total: 0,
                job: {},
                enq: {},
                task: {}
            },
            allocatedHoursForPeriod: {
                overdue: {total: 0},
                date: [],
                total: 0,
            },
            proposedHoursForPeriod: {
                overdue: baseUtilisationData.staff[staff.callName]?.['proposedHoursForPeriod']?.['overdue'],
                date: [],
                total: 0,
                job: {},
                enq: {},
                task: {}
            },
            prospectHoursForPeriod: {
                overdue: baseUtilisationData.staff[staff.callName]?.['prospectHoursForPeriod']?.['overdue'],
                date: [],
                total: 0,
                job: {},
                enq: {},
                task: {}
            },
            leaveHours: filters.interval === 'daily' ? baseUtilisationData.staff[staff.callName]?.['leaveHours'] :
                {date: [], total: 0},
            periodHours: []
        }
    })
    if (filters.interval != 'daily') {
        let adjustedIndex = 0;
        let currentPeriodIndex = 0;
        let sundayCount = 0;

        const length = dateIndexesWithDateObjects.length;
        for (let i = 0; i < length; i++) {
            const date = dateIndexesWithDateObjects[i];
            const utcDay = date.getUTCDay();

            Object.values(staff).forEach(staff => {
                if (!staff.teamId) return;
                if (!utilisationData.staff[staff.callName].periodHours[currentPeriodIndex]) utilisationData.staff[staff.callName].periodHours[currentPeriodIndex] = 0;
                utilisationData.staff[staff.callName].periodHours[currentPeriodIndex] +=
                    staff.hoursPerDay[utcDay] * (i in (baseUtilisationData.workDayDates?.[staff.callName] || []) ? baseUtilisationData.workDayDates[staff.callName][i] : 1);

                utilisationData.staff[staff.callName]['leaveHours']["total"] += baseUtilisationData.staff?.[staff.callName]?.['leaveHours']?.['date'][i] || 0;

                if (!utilisationData.staff[staff.callName]['leaveHours']['date'][currentPeriodIndex])
                    utilisationData.staff[staff.callName]['leaveHours']['date'][currentPeriodIndex] = 0;
                utilisationData.staff[staff.callName].leaveHours.date[currentPeriodIndex] += baseUtilisationData.staff?.[staff.callName]?.['leaveHours']?.['date'][i] || 0;
            })

            dateIndexToAdjustedIndexMap[i] = currentPeriodIndex;
            // dateIndexToAdjustedIndexMap[i] = adjustedIndex;

            if (utcDay === 0) sundayCount++;
            if (
                i === 0 ||
                filters.interval === 'weekly' && sundayCount === 1 ||
                filters.interval === 'fortnightly' && sundayCount === 2 ||
                filters.interval === 'monthly' && date.getUTCDate() === 1
            ) {
                utilisationData.dateIndexes[adjustedIndex] = date;
                if (i > 0) {
                    dateIndexesForPeriodStartDates[i] = adjustedIndex;
                    currentPeriodIndex++;
                    dateIndexToAdjustedIndexMap[i] = currentPeriodIndex;
                }

                adjustedIndex++;
                sundayCount = 0;
            }
        }
    }
    const length = utilisationData.dateIndexes.length;
    Object.values(staff).forEach(staff => {
        if (!staff.teamId) return;
        for (let i = 0; i < length; i++) {
            // if (!(i in (baseUtilisationData?.workDayDates?.[staff.callName] || []))) return;

            let date = null;
            let utcDay = null;
            if (filters.interval === 'daily') {
                date = dateIndexesWithDateObjects[i];
                utcDay = date.getUTCDay();
            }

            const periodHoursForDateIndex = filters.interval === 'daily' ?
                staff.hoursPerDay[utcDay] * (i in (baseUtilisationData.workDayDates?.[staff.callName] || []) ? baseUtilisationData.workDayDates[staff.callName][i] : 1) :
                utilisationData.staff[staff.callName].periodHours[i];

            // if (filters.normalTasks || filters.proposedTasks) {
            utilisationData.group[staff.teamId].periodHours[i] =
                (utilisationData.group[staff.teamId].periodHours[i] || 0) + (periodHoursForDateIndex || 0);

            if (staff.leaderOfId && utilisationData.group[staff.leaderOfId]) {
                utilisationData.group[staff.leaderOfId].periodHours[i] =
                    (utilisationData.group[staff.leaderOfId].periodHours[i] || 0) + (periodHoursForDateIndex || 0);
            }

            if (!utilisationData.group['mel'].periodHours[i]) utilisationData.group['mel'].periodHours[i] = 0;
            utilisationData.group['mel'].periodHours[i] += (periodHoursForDateIndex || 0);
            if (filters.leave) {
                utilisationData.group[staff.teamId].leaveHours.date[i] =
                    (utilisationData.group[staff.teamId].leaveHours.date[i] || 0) + (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);
                utilisationData.group[staff.teamId].leaveHours.total += (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);

                if (staff.leaderOfId && utilisationData.group[staff.leaderOfId]) {
                    utilisationData.group[staff.leaderOfId].leaveHours.date[i] =
                        (utilisationData.group[staff.leaderOfId].leaveHours.date[i] || 0) + (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);
                    utilisationData.group[staff.leaderOfId].leaveHours.total += (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);
                }

                utilisationData.group['mel'].leaveHours.date[i] =
                    (utilisationData.group['mel'].leaveHours.date[i] || 0) + (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);
                utilisationData.group['mel'].leaveHours.total += (utilisationData.staff[staff.callName]?.leaveHours?.date[i] || 0);
            }
        }
    })
    // dayjs.tz.setDefault('Pacific/Auckland');
    const dateIndexesByTs = baseUtilisationData['dateIndexesByTs'];
    // const today = dayjs.tz().set('hour', 0).set('minute', 0).set('second', 0);
    const todayUnix = baseUtilisationData.dateIndexes[0];

    const newArrayLength = utilisationData.dateIndexes.length + 1;
    Object.values(tasks).forEach((task) => {
        // for (const task of Object.values(tasks)) {
        const startDate = new Date(task.startDate.slice(0, -6) + '+00:00');
        const endDate = new Date(task.jotReqDate.slice(0, -6) + '+00:00');


        const endDateUnixSeconds = Math.floor(endDate.valueOf() / 1000);
        const startDateUnixSeconds = Math.max(Math.floor(startDate.valueOf() / 1000), todayUnix);

        let normalProposed: 'proposedHoursForPeriod' | 'normalHoursForPeriod' | 'prospectHoursForPeriod'
            = (task.proposedTask || task.onHold) ? 'proposedHoursForPeriod' : 'normalHoursForPeriod';
        if (task.fkEnqid && task.proposedTask && enqs[task.fkEnqid].isProspectEnquiry) normalProposed = 'prospectHoursForPeriod';


        if (normalProposed === 'normalHoursForPeriod' && !filters.normalTasks) return;
        if (normalProposed === 'proposedHoursForPeriod' && !filters.proposedTasks) return;
        if (normalProposed === 'prospectHoursForPeriod' && !filters.prospectTasks) return;

        if (!baseUtilisationData.workDayDates[task.jotAllocatedto]) return;
        if (!staff[task.jotAllocatedto]?.teamId) return;

        if (todayUnix > endDateUnixSeconds) {
            utilisationData.staff[task.jotAllocatedto].allocatedHoursForPeriod.overdue.total += task.remainingHours;
            utilisationData.group[staff[task.jotAllocatedto].teamId].allocatedHoursForPeriod.overdue.total += task.remainingHours;

            if (staff[task.jotAllocatedto].leaderOfId && utilisationData.group[staff[task.jotAllocatedto].leaderOfId]) {
                utilisationData.group[staff[task.jotAllocatedto].leaderOfId].allocatedHoursForPeriod.overdue.total += task.remainingHours;
            }

            utilisationData.group['mel'].allocatedHoursForPeriod.overdue.total += task.remainingHours;
            return
        }


        const taskHoursPerDay = filters.allocatedForecast === 'allocated' ? task['cachedRemainingHoursPerDay'] : task['cachedForecastRemainingHoursPerDay'];

        let taskHours = 0;
        // let adjustedIndex = 0;
        let adjustedIndex = dateIndexToAdjustedIndexMap[dateIndexesByTs[startDateUnixSeconds]] || 0;
        // const newArrayLength =  dateIndexesByTs[endDateUnixSeconds] - dateIndexesByTs[startDateUnixSeconds] + 1;
        for (let i = dateIndexesByTs[startDateUnixSeconds]; i <= dateIndexesByTs[endDateUnixSeconds]; i++) {
            if (filters.interval != 'daily') {
                taskHours += (taskHoursPerDay * baseUtilisationData.workDayDates[task.jotAllocatedto][i]) || 0;

                if (dateIndexesForPeriodStartDates[i + 1] || i == dateIndexesByTs[endDateUnixSeconds]) {
                    populateHoursForIndex(adjustedIndex, task, taskHours, normalProposed, utilisationData, staff, newArrayLength);


                    adjustedIndex++;
                    taskHours = 0;
                }

            } else {
                taskHours = taskHoursPerDay * baseUtilisationData.workDayDates[task.jotAllocatedto][i];
                populateHoursForIndex(i, task, taskHours, normalProposed, utilisationData, staff, newArrayLength);
            }

        }
    })


    return utilisationData;
}

const populateHoursForIndex = (i: number, task: Task, taskHours: number, normalProposed: 'proposedHoursForPeriod' | 'normalHoursForPeriod' | 'prospectHoursForPeriod', utilisationData: StaffUtilisationDataSet, staff: Record<string, Staff>, arrayLength: number) => {
    utilisationData.group['mel'].allocatedHoursForPeriod.date[i] =
        (utilisationData.group['mel'].allocatedHoursForPeriod.date[i] || 0) + taskHours;
    utilisationData.group['mel'].allocatedHoursForPeriod.total += taskHours;

    utilisationData.group[staff[task.jotAllocatedto].teamId].allocatedHoursForPeriod.date[i] =
        (utilisationData.group[staff[task.jotAllocatedto].teamId].allocatedHoursForPeriod.date[i] || 0) + taskHours;
    utilisationData.group[staff[task.jotAllocatedto].teamId].allocatedHoursForPeriod.total += taskHours;

    if (staff[task.jotAllocatedto].leaderOfId && utilisationData.group[staff[task.jotAllocatedto].leaderOfId]) {
        utilisationData.group[staff[task.jotAllocatedto].leaderOfId].allocatedHoursForPeriod.date[i] =
            (utilisationData.group[staff[task.jotAllocatedto].leaderOfId].allocatedHoursForPeriod.date[i] || 0) + taskHours;
        utilisationData.group[staff[task.jotAllocatedto].leaderOfId].allocatedHoursForPeriod.total += taskHours;
    }

    // return;

    utilisationData.staff[task.jotAllocatedto]['allocatedHoursForPeriod']["total"] += taskHours;
    if (!utilisationData.staff[task.jotAllocatedto]['allocatedHoursForPeriod']["date"][i]) utilisationData.staff[task.jotAllocatedto]['allocatedHoursForPeriod']["date"][i] = 0;
    utilisationData.staff[task.jotAllocatedto]['allocatedHoursForPeriod']["date"][i] += taskHours;

    utilisationData.staff[task.jotAllocatedto][normalProposed]["total"] += taskHours;
    if (!utilisationData.staff[task.jotAllocatedto][normalProposed]["date"][i]) utilisationData.staff[task.jotAllocatedto][normalProposed]["date"][i] = 0;
    utilisationData.staff[task.jotAllocatedto][normalProposed]["date"][i] += taskHours;

    if (task.fkJobid) {
        if (!utilisationData.staff[task.jotAllocatedto][normalProposed]['job'][task.fkJobid]) utilisationData.staff[task.jotAllocatedto][normalProposed]['job'][task.fkJobid] = Array(arrayLength);//new Float32Array(arrayLength);
        if (!utilisationData.staff[task.jotAllocatedto][normalProposed]['job'][task.fkJobid][i]) utilisationData.staff[task.jotAllocatedto][normalProposed]['job'][task.fkJobid][i] = 0;
        utilisationData.staff[task.jotAllocatedto][normalProposed]['job'][task.fkJobid][i] += taskHours;
    }
    if (task.fkEnqid) {
        if (!utilisationData.staff[task.jotAllocatedto][normalProposed]['enq'][task.fkEnqid]) utilisationData.staff[task.jotAllocatedto][normalProposed]['enq'][task.fkEnqid] = Array(arrayLength);//new Float32Array(arrayLength);
        if (!utilisationData.staff[task.jotAllocatedto][normalProposed]['enq'][task.fkEnqid][i]) utilisationData.staff[task.jotAllocatedto][normalProposed]['enq'][task.fkEnqid][i] = 0;
        utilisationData.staff[task.jotAllocatedto][normalProposed]['enq'][task.fkEnqid][i] += taskHours;
    }
    if (!utilisationData.staff[task.jotAllocatedto][normalProposed]['task'][task.pkJobTicketid]) utilisationData.staff[task.jotAllocatedto][normalProposed]['task'][task.pkJobTicketid] = Array(arrayLength);//new Float32Array(arrayLength);
    utilisationData.staff[task.jotAllocatedto][normalProposed]['task'][task.pkJobTicketid][i] = taskHours;
}

function generateMenuNodes(teams: Team[], staff: {
    [staffCallname: string]: Staff
}, utilisationData: StaffUtilisationDataSet, jobs: Job[], enqs: Record<number, Enquiry>, tasks: Record<string, Task>, roles: Record<string, Role>, staffScheduleOnly = false): MenuNode[] {
    const menuNodes: MenuNode[] = [];

    const populateStaffJobsEnqLeave = (stfCallname: string, arraySubSection: 'normalHoursForPeriod' | 'proposedHoursForPeriod' | 'prospectHoursForPeriod' | 'leaveHours', subType): MenuNode[] => {

        if (subType === 'Leave') return [];


        const getTaskMenuNodeFromTask = (task: Task): MenuNode => {
            return {
                rootLevelGroup: 'staff',
                rootLevelId: stfCallname,
                staffCallname: stfCallname,
                hoursType: arraySubSection,
                id: task.pkJobTicketid,
                key: `${stfCallname}.task.${task.pkJobTicketid}`,
                level: 4,
                subLevelGroup: 'task',
                name: `${task.taskCode} ${task.jotDescText?.split('\n')[0] || ''}`
            }
        }


        const menuNodes: MenuNode[] = [];

        const staffTasks: Task[] = Object.keys({
                ...utilisationData.staff[stfCallname]?.[arraySubSection]?.task,
                ...utilisationData.staff[stfCallname]?.[arraySubSection]?.overdue?.task,
            }
        ).map(taskId => tasks[taskId]);


        Object.keys({
            ...utilisationData.staff[stfCallname]?.[arraySubSection]?.job,
            ...utilisationData.staff[stfCallname]?.[arraySubSection]?.overdue?.job,
        }).forEach(jobId => {
            if (jobId == 'total') return;
            menuNodes.push({
                rootLevelGroup: 'staff',
                rootLevelId: stfCallname,
                staffCallname: stfCallname,
                hoursType: arraySubSection,
                cliCode: jobs[jobId].cliCode,
                id: jobs[jobId].pkJobid,
                name: jobs[jobId].jobRef + ' - ' + jobs[jobId].jobTitle,
                key: `${stfCallname}.job.${jobs[jobId].pkJobid}`,
                level: 3,
                subLevelGroup: 'job',
                children: staffTasks.filter(task => task.fkJobid == jobId).map(task => getTaskMenuNodeFromTask(task))
            })
        });

        Object.keys({
            ...utilisationData.staff[stfCallname]?.[arraySubSection]?.enq,
            ...utilisationData.staff[stfCallname]?.[arraySubSection]?.overdue?.enq,
        }).forEach(enqId => {
            if (enqId == 'total') return;
            menuNodes.push({
                staffCallname: stfCallname,
                hoursType: arraySubSection,
                key: `${stfCallname}.${arraySubSection}.enq.${enqId}`,
                rootLevelGroup: 'staff',
                rootLevelId: stfCallname,
                cliCode: enqs[enqId].cliCode,
                id: enqs[enqId].pkEnquiryid,
                name: `EN${enqId} - ${enqs[enqId].enqTitle}`,
                level: 3,
                subLevelGroup: 'enq',
                children: staffTasks.filter(task => task.fkEnqid == enqId).map(task => getTaskMenuNodeFromTask(task))
            })
        });

        return menuNodes;
    }

    const populateSubMenusForStaff = (stfCallname): MenuNode[] => {
        // subType: 'Normal Tasks' | 'Proposed Tasks' | 'Leave'
        return ['Normal Tasks', 'Proposed Tasks', 'Prospect Tasks', 'Leave'].map((v): MenuNode => {
            const arraySubSection =
                v === 'Normal Tasks' ?
                    'normalHoursForPeriod' :
                    v === 'Leave' ?
                        'leaveHours' :
                        v === 'Prospect Tasks' ?
                            'prospectHoursForPeriod' :
                                'proposedHoursForPeriod';
            return {
                rootLevelGroup: 'staff',
                rootLevelId: stfCallname,
                id: v,
                hoursType: arraySubSection,
                key: `${stfCallname}.${v}`,
                staffCallname: stfCallname,
                subLevelGroup: 'staff',
                level: 2,
                name: v,
                children: populateStaffJobsEnqLeave(stfCallname, arraySubSection, v)
            }
        })
    }

    if (staffScheduleOnly) {
        const selectedStaff = Object.values(staff)[0];

        menuNodes.push({
            id: selectedStaff.callName,
            key: selectedStaff.callName,
            rootLevelGroup: 'staff',
            rootLevelId: selectedStaff.callName,
            hoursType: "allocatedHoursForPeriod",
            staffCallname: selectedStaff.callName,
            subLevelGroup: 'staff',
            level: 1,
            name: selectedStaff.firstName + ' ' + selectedStaff.lastName,
            children: populateSubMenusForStaff(selectedStaff.callName)
        })
    } else {
        menuNodes.push({
            id: 'mel',
            key: 'mel',
            subLevelGroup: 'group',
            rootLevelGroup: 'group',
            rootLevelId: 'mel',
            level: 0,
            name: 'MEL',
            hoursType: 'allocatedHoursForPeriod',
            open: true,
            children: teams.map(team => {
                return {
                    id: team.id,
                    key: team.id,
                    rootLevelGroup: 'group',
                    rootLevelId: team.id,
                    hoursType: 'allocatedHoursForPeriod',
                    subLevelGroup: 'group',
                    level: 0,
                    name: team.name,
                    open: false,
                    children: Object.values(staff).filter(staff => (team?.leaderId === staff.id || staff?.team === team.name)).map(staff => ({
                        id: staff.callName,
                        key: team.id + staff.callName,
                        rootLevelGroup: 'staff',
                        rootLevelId: staff.callName,
                        hoursType: "allocatedHoursForPeriod",
                        staffCallname: staff.callName,
                        subLevelGroup: 'staff',
                        level: 1,
                        name: staff.firstName + ' ' + staff.lastName,
                        children: populateSubMenusForStaff(staff.callName)
                    }))
                }
            })
        })
    }

    const _addErrorMessagesAndPostProcess = (menuNodes): string => {
        let returnString = '';
        let currentErrorString = '';
        menuNodes.forEach((node: MenuNode) => {
            if (node.children) returnString = _addErrorMessagesAndPostProcess(node.children);
            // if (node.children) cleanupMenuNodeCfgs(node.children);

            if (node.subLevelGroup === 'task' && tasks[node.id].remainingHours && !utilisationData.staff[node.staffCallname]?.[node.hoursType]?.overdue?.task?.[node.id] && !sumArray(utilisationData.staff[node.staffCallname]?.[node.hoursType]?.task?.[node.id])) {
                node.errorString = `This task has ${tasks[node.id].remainingHours} hours remaining but these are not  being counted towards Utilisation because all hours are allocated over a holiday period`;

                currentErrorString = 'Hours in a child task are not being counted towards Utilisation because all hours are allocated over a holiday period';
            } else if (returnString) {
                if (node.subLevelGroup === 'group') returnString = '';

                currentErrorString = returnString;
                node.errorString = returnString;

                if (node.subLevelGroup === 'staff' && node.hoursType === 'allocatedHoursForPeriod') {
                    returnString = '';
                    currentErrorString = '';
                }
            }

            if (staffScheduleOnly) {
                node.level = node.level - 1;
            }

        })
        return currentErrorString;
    }
    _addErrorMessagesAndPostProcess(menuNodes);

    return menuNodes;
}

function cleanupMenuNodeCfgs(menuNodeCfgSet: MenuNodeCfgSet, menuNodes: MenuNode[], staffScheduleOnly: boolean, tasks, utilisationData) {
    console.log('cleaning up');
    const cleanedUpMenuCfgs = (staffScheduleOnly ?
            {
                [menuNodes[0].id]: {
                    open: true,
                    graph: true
                }
            }
            :
            {
                'mel': {
                    open: true
                },
            }
    );
    /**
     * This is to prevent expired menu items from having stale config items stored
     * @param menuNodes
     */
    const _cleanupMenuNodeCfgs = (menuNodes): string => {
        menuNodes.forEach((node: MenuNode) => {
            if (menuNodeCfgSet[node.key]) cleanedUpMenuCfgs[node.key] = menuNodeCfgSet[node.key];

            if (node.children) _cleanupMenuNodeCfgs(node.children);
        })
    }
    if (menuNodeCfgSet instanceof Object) {
        _cleanupMenuNodeCfgs(menuNodes);
        cleanedUpMenuCfgs['Average'] = menuNodeCfgSet['Average'];
    }

    return cleanedUpMenuCfgs;
}

const generateMenuNodesFlatToRender = (menuNodes: MenuNode[], filters: FilterSet, menuNodeCfgs: MenuNodeCfgSet, filteredUtilisationData: StaffUtilisationDataSet) => {
    const menuNodesToRender: MenuNode[] = [];
    const menuNodesToGraph: MenuNode[] = [];

    for (const _menuNode of menuNodes) {
        let menuNode = _menuNode;

        if (!filters.overdueTasks && (menuNode.subLevelGroup === 'task' || menuNode.subLevelGroup === 'job' || menuNode.subLevelGroup === 'enq')) {
            // if (!sumArray(filteredUtilisationData['staff'][menuNode.staffCallname]?.[menuNode.hoursType]?.[menuNode.subLevelGroup]?.[menuNode.id])) continue;
            if (filteredUtilisationData['staff'][menuNode.staffCallname]?.[menuNode.hoursType]?.overdue?.[menuNode.subLevelGroup]?.[menuNode.id]) continue;
        }
        if (!filters.leave && menuNode.name === 'Leave') continue;
        if (!filters.proposedTasks && menuNode.name === 'Proposed Tasks') continue;
        if (!filters.prospectTasks && menuNode.name === 'Prospect Tasks') continue;
        if (!filters.normalTasks && menuNode.name === 'Normal Tasks') continue;

        if (menuNode.subLevelGroup === 'group' && menuNode.key != 'mel' && filters.leave && !filters.normalTasks && !filters.proposedTasks) {
            menuNode = {
                ...menuNode,
                hoursType: 'leaveHours',
                children: menuNode.children.map(child => ({
                    ...child.children.find(m => m.name === 'Leave'),
                    key: child.key,
                    name: child.name + ' Leave',
                    hoursType: 'leaveHours',
                    level: 1
                }))

            }
        }

        const cfg = menuNodeCfgs[menuNode.key] || {};
        menuNodesToRender.push(menuNode);
        if (cfg.graph && menuNode.level <= 1) menuNodesToGraph.push(menuNode);


        if (menuNode.children && cfg.open) {
            // console.log('menunode open', menuNode);
            const result = generateMenuNodesFlatToRender(menuNode.children.map((_menuNode, i) => ({
                ..._menuNode,
                sortGroupKey: menuNode.key,
                sort: menuNodeCfgs[_menuNode.key]?.sort,
                index: i
            })).toSorted(menuNodeSorter), filters, menuNodeCfgs, filteredUtilisationData);
            menuNodesToRender.push(...result[0]);
            menuNodesToGraph.push(...result[1]);

        }
    }
    return [menuNodesToRender, menuNodesToGraph];
}

function isNumber(a) {
    return typeof (a) === 'number';
}

function menuNodeSorter(a, b) {
    // console.log(`a: ${a.name}, b: ${b.name}`);
    if (isNumber(a.sort) && isNumber(b.sort)) {
        // console.log('both have sort set');
        if (a.sort > b.sort) return 1;
        if (a.sort < b.sort) return -1;
        return 0;
    }
    if ((isNumber(a.sort) && b.index < a.sort) || (isNumber(b.sort) && a.index > b.sort)) {
        // console.log('1');
        return 1;
    }
    if ((isNumber(a.sort) && b.index >= a.sort) || (isNumber(b.sort) && a.index <= b.sort)) {
        // console.log('-1');
        return -1;
    }
    // console.log('equal');
    return 0;
}


window["renderUtilisationWidget"] = function (utilisationDto) {
    createRoot(document.querySelector('#utilisationWidget')).render(
        <Utilisation
            {...utilisationDto}
        />);

}
